import { isAccountLinked, filterAccountsByRestrictions, analyzeFipAndAddToStableList } from './helper.utils';
import { getTargetedAccounts } from './targetedAccounts.utils';
import { trackEvent } from './tracking.utils';
import {
	captureDiscoveryRecordNotFound,
	captureDiscoveryCompleted,
	captureDiscoveryError,
	captureAllDiscoveriesCompleted,
} from './analytics/posthog.utils';

/**
 * Simulates API latency with a random delay
 * @param {number} minDelay - Minimum delay in milliseconds
 * @param {number} maxDelay - Maximum delay in milliseconds
 * @returns {Promise} Promise that resolves after the delay
 */
const simulateLatency = (minDelay = 5000, maxDelay = 8000) => {
	const delay =
		Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
	return new Promise((resolve) => setTimeout(resolve, delay));
};

/**
 * Gets linked accounts that were not discovered for a specific FIP
 * @param {Array} linkedaccFull - Full list of linked accounts
 * @param {string} fid - FIP ID
 * @returns {Array} Array of linked accounts not discovered
 */
const getLinkedAccountsNotDiscovered = (linkedaccFull, fid) => {
	return linkedaccFull
		.filter((el) => el.fipId === fid)
		.map(({ accType, accRefNumber, maskedAccNumber, FIType }) => ({
			accType,
			accRefNumber,
			maskedAccNumber,
			FIType,
		}));
};

// getTargetedAccounts function moved to targetedAccounts.utils.js

/**
 * Prepares FIP identifiers for account discovery
 * @param {Object} fipIdentifiersInfo - FIP identifier information
 * @param {string} phoneNumber - User's phone number
 * @param {string} pan - User's PAN
 * @param {string} dob - User's DOB
 * @param {boolean} isPanRequired - Whether PAN is required
 * @param {boolean} isDobRequired - Whether DOB is required
 * @returns {Array} Array of FIP identifiers
 */
const prepareFipIdentifiers = (
	fipIdentifiersInfo,
	phoneNumber,
	pan,
	dob,
	isPanRequired,
	isDobRequired
) => {
	const fipIdentifiers = [];
	let idValue = phoneNumber;

	for (const [key, value] of Object.entries(fipIdentifiersInfo)) {
		if (key === 'PAN') {
			idValue = pan;
		}
		if (key === 'DOB') {
			idValue = dob;
		}
		if (
			(key === 'PAN' && !isPanRequired) ||
			(key === 'DOB' && !isDobRequired)
		) {
			continue;
		}
		fipIdentifiers.push({ category: value, type: key, value: idValue });
	}

	return fipIdentifiers;
};

/**
 * Processes discovered accounts for a FIP
 * @param {Object} res - API response
 * @param {Array} linkedaccFull - Full list of linked accounts
 * @param {string} fid - FIP ID
 * @param {string} phoneNumber - User's phone number
 * @returns {Object} Processed FIP data
 */
const processDiscoveredAccounts = (res, linkedaccFull, fid, phoneNumber) => {
	analyzeFipAndAddToStableList(fid, res.DiscoveredAccounts, linkedaccFull);
	const notLinkedList = res.DiscoveredAccounts.filter(
		(acc) => !isAccountLinked(acc, linkedaccFull,fid)
	);
	const filterAccounts = filterAccountsByRestrictions(notLinkedList);
	const notLinkedDiscoveredAccounts = global?.accountRestrictions
		? filterAccounts
		: notLinkedList;

	// Track events
	if (notLinkedList.length > 0) {
		trackEvent('UNLINKED_ACCOUNTS_DISCOVERED', {
			fipId: fid,
			count: notLinkedList.length,
		});
	}
	if (res.DiscoveredAccounts.length - notLinkedList.length > 0) {
		trackEvent('LINKED_ACCOUNTS_DISCOVERED', {
			fipId: fid,
			count: res.DiscoveredAccounts.length - notLinkedList.length,
		});
	}

	return {
		fipObj: {
			fipId: fid,
			discoveredAccounts: global?.accountRestrictions
				? filterAccountsByRestrictions(res?.DiscoveredAccounts)
				: res.DiscoveredAccounts,
			phoneNumber: phoneNumber,
		},
		notLinkedDiscoveredAccounts,
		noResultsFips: [],
	};
};

/**
 * Processes failed or no-record FIP response
 * @param {Object} res - API response
 * @param {Array} linkedaccFull - Full list of linked accounts
 * @param {string} fid - FIP ID
 * @param {string} phoneNumber - User's phone number
 * @returns {Object} Processed FIP data
 */
const processFailedFipResponse = (res, linkedaccFull, fid, phoneNumber) => {
	const linkedAccountsNotDiscovered = getLinkedAccountsNotDiscovered(
		linkedaccFull,
		fid
	);
	const fipObj = {
		fipId: fid,
		discoveredAccounts: global?.accountRestrictions
			? filterAccountsByRestrictions(linkedAccountsNotDiscovered)
			: linkedAccountsNotDiscovered,
		phoneNumber: phoneNumber,
	};

	const noResultsFips = [];
	if (
		global?.accountRestrictions &&
		filterAccountsByRestrictions(linkedAccountsNotDiscovered).length === 0
	) {
		noResultsFips.push(fid);
	} else if (linkedAccountsNotDiscovered.length === 0) {
		noResultsFips.push(fid);
	}

	return { fipObj, notLinkedDiscoveredAccounts: [], noResultsFips };
};

/**
 * Updates account discovery state
 * @param {Object} prevState - Previous state
 * @param {Object} fipData - FIP data to update
 * @param {string} phoneNumber - User's phone number
 * @param {Function} history - History object for navigation
 * @param {Map} pendingDiscoveries - Map of ongoing discoveries
 * @returns {Object} Updated state
 */
const updateAccountDiscoveryState = (
	prevState,
	fipData,
	phoneNumber,
	history,
	pendingDiscoveries
) => {
	const { fipObj, notLinkedDiscoveredAccounts, noResultsFips } = fipData;
	const fid = fipObj.fipId;

	let newDiscoveredAccounts = [...prevState.discoveredAccounts];
	let newNoResultsFips = [...(prevState.noDiscoveredAccounts?.fips || [])];

	// Only add to discovered accounts if we have accounts
	if (fipObj.discoveredAccounts.length > 0) {
		newDiscoveredAccounts = [
			...newDiscoveredAccounts.filter((acc) => acc.fipId !== fid),
			fipObj,
		].sort((a, b) => b.discoveredAccounts.length - a.discoveredAccounts.length);
	} else {
		// Remove from discovered accounts if it was there
		newDiscoveredAccounts = newDiscoveredAccounts.filter(
			(acc) => acc.fipId !== fid
		);
		// Add to no results if not already there
		if (!newNoResultsFips.includes(fid)) {
			newNoResultsFips.push(fid);
		}
	}
	// Calculate total selected accounts across all FIPs
	const totalSelectedAccounts = [
		...prevState.selectedAccounts.filter((acc) => acc.fipId !== fid),
		...notLinkedDiscoveredAccounts,
	];

	// Check if no accounts found across all FIPs
	const noAccountsFoundAcrossAllFips = 
		global.sdkOptions?.terminateOnNoAccountFound === 'true' &&
		history?.location?.state?.data &&
		totalSelectedAccounts.length === 0 &&
		prevState.discoveredAccounts.length === 0 &&
		newDiscoveredAccounts.length === 0 &&
		pendingDiscoveries.size === 0;
	
	if (noAccountsFoundAcrossAllFips) {
		const allNoResultFips = [...newNoResultsFips];
		if (!allNoResultFips.includes(fid) && noResultsFips.includes(fid)) {
			allNoResultFips.push(fid);
		}

		history.push('/exit', {
			fipIds: allNoResultFips
		});
	}
	// Check account restrictions satisfaction if they exist
	if (global.redirectAccountRestrictions && history?.location?.state?.data && prevState.linkedAccountsFull) {
		const allRestrictionsSatisfied =
			global.redirectAccountRestrictions.every((restriction) =>
				prevState.linkedAccountsFull.some(
					(account) =>
						account.FIType?.slice(-4) === restriction.fiType &&
						account.accType === restriction.accType &&
						account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber
				)
			);

		if (allRestrictionsSatisfied) {
			history.push(`/payment-consent`);
			return prevState; // Early return if redirecting
		}
	}

	// Apply targeted account filtering if restrictions exist
	const finalDiscoveredAccounts = global?.redirectAccountRestrictions
		? getTargetedAccounts(newDiscoveredAccounts, global.redirectAccountRestrictions)
		: newDiscoveredAccounts;

	// For targeted accounts, also add FIPs that had accounts but no targeted matches
	if (global?.redirectAccountRestrictions) {
		const fipHadAccounts = newDiscoveredAccounts.some(fip => fip.fipId === fid && fip.discoveredAccounts.length > 0);
		const fipHasTargetedAccounts = finalDiscoveredAccounts.some(fip => fip.fipId === fid && fip.discoveredAccounts.length > 0);
		
		if (fipHadAccounts && !fipHasTargetedAccounts && !newNoResultsFips.includes(fid)) {
			newNoResultsFips.push(fid);
		}
	}
	// Check if we should redirect
	if (
		global.sdkOptions?.hideNoAccountFound === 'true' &&
		history?.location?.state?.data &&
		prevState.discoveredAccounts.length === 0 &&
		finalDiscoveredAccounts.length === 0 &&
		pendingDiscoveries.size === 0
	) {
		history.push(`/home/?number=${phoneNumber}&isAddAccount=${true}`);
	}

	return {
		...prevState,
		discoveredAccounts: finalDiscoveredAccounts,
		isLoding: false,
		isDiscovering: pendingDiscoveries.size > 0,
		selectedAccounts: [
			...prevState.selectedAccounts.filter((acc) => acc.fipId !== fid),
			...notLinkedDiscoveredAccounts,
		],
		noDiscoveredAccounts: {
			fips: global.sdkOptions?.hideNoAccountFound && history?.location?.state?.data 
				? [] 
				: newNoResultsFips,
			phoneNumber: phoneNumber,
		},
		noAccountsFoundAcrossAllFips,
	};
};

/**
 * Handles discovery for a single FIP
 * @param {Object} params - Parameters for FIP discovery
 * @returns {Promise} Promise resolving to processed FIP data
 */
const handleFipDiscovery = async ({
	fid,
	fipIdentifiersInfo,
	phoneNumber,
	pan,
	dob,
	isPanRequired,
	isDobRequired,
	linkedaccFull,
	authApis,
	pendingDiscoveries,
}) => {
	const fipIdentifiers = prepareFipIdentifiers(
		fipIdentifiersInfo,
		phoneNumber,
		pan,
		dob,
		isPanRequired,
		isDobRequired
	);

	pendingDiscoveries.set(fid, true);
	const fipStartTime = Date.now();

	try {
		const res = await authApis.discoverAccountsAsync(fid, fipIdentifiers);
		
		if (!res) {
			throw new Error('No response from server');
		}

		let fipData;
		if (res?.status === 'SUCCESS') {
			fipData = processDiscoveredAccounts(res, linkedaccFull, fid, phoneNumber);
			captureDiscoveryCompleted(fid, {
				discoveryTime: Date.now() - fipStartTime,
				accountsFound: res?.DiscoveredAccounts?.length || 0,
				status: res?.status,
				isFirstFip: pendingDiscoveries.size === 1,
				totalPendingFips: pendingDiscoveries.size - 1,
				isAsync: true,
			});
		} else if (
			res?.status === 'RECORD-NOT-FOUND' ||
			res?.status === 'FAILURE'
		) {
			if (res?.status === 'RECORD-NOT-FOUND') {
				captureDiscoveryRecordNotFound(fid, {
					discoveryTime: Date.now() - fipStartTime,
					isFirstFip: pendingDiscoveries.size === 1,
					totalPendingFips: pendingDiscoveries.size - 1,
					isAsync: true,
				});
			} else {
				captureDiscoveryError(fid, {
					error: res?.message || 'Unknown error',
					discoveryTime: Date.now() - fipStartTime,
					isFirstFip: pendingDiscoveries.size === 1,
					isAsync: true,
				});
			}
			fipData = processFailedFipResponse(res, linkedaccFull, fid, phoneNumber);
		} else {
			if (res?.status === 'FAILURE' && res?.message === 'Session Error.') {
				throw new Error('Session Error');
			}
			fipData = {
				fipObj: {
					fipId: fid,
					discoveredAccounts: [],
					phoneNumber: phoneNumber,
				},
				notLinkedDiscoveredAccounts: [],
				noResultsFips: [fid],
			};
		}

		// Track completion of all discoveries if this was the last pending FIP
		if (pendingDiscoveries.size === 1) {
			captureAllDiscoveriesCompleted({
				totalTime: Date.now() - fipStartTime,
				totalFips: pendingDiscoveries.size,
				isAsync: true,
			});
		}

		return fipData;
	} catch (error) {
		captureDiscoveryError(fid, {
			error: error.message || 'Unknown error',
			discoveryTime: Date.now() - fipStartTime,
			isFirstFip: pendingDiscoveries.size === 1,
			isAsync: true,
		});
		throw error;
	} finally {
		pendingDiscoveries.delete(fid);
	}
};

export {
	handleFipDiscovery,
	updateAccountDiscoveryState,
	prepareFipIdentifiers,
	processDiscoveredAccounts,
	processFailedFipResponse,
	getLinkedAccountsNotDiscovered,
};
